import * as React from "react";
import { Link } from "react-router-dom";
import { Card, Image, Tag, Typography } from "antd";
import { InfoCircleOutlined, UnlockOutlined } from "@ant-design/icons";

// common
import {
  Marker,
  Note,
  ProgressCircle,
  ImageDescription,
  IndexList,
  List,
} from "../../common";

const list: List[] = [
  {
    title: "デバイスセキュリティ",
    children: [
      {
        title: "画面ロックをかける",
        children: [{ title: "画面ロックの時間を変更する" }],
      },
      {
        title: "紛失や盗難に備える",
        children: [{ title: "遠隔でロックする" }],
      },
      {
        title: "暗号化する",
        children: [{ title: "スマホ端末を暗号化する" }],
      },
    ],
  },
  {
    title: "アプリケーションセキュリティ",
    children: [
      {
        title: "信頼するものを選ぶ",
        children: [{ title: "モバイルアプリのインストールを制限する" }],
      },
      {
        title: "アクセス権限を最小限にする",
        children: [{ title: "アプリの権限を確認する" }],
      },
    ],
  },
  {
    title: "ネットワークセキュリティ",
    children: [
      {
        title: "暗号化されたウェブサイトと通信する",
        children: [
          { title: "HTTPとHTTPS" },
          {
            title: "SSL証明書を確認する",
          },
          {
            title: "さらに安全なEV-SSL証明書の登場",
          },
        ],
      },
      {
        title: "Bluetoothを安全に使用する",
      },
      {
        title: "無線LANを安全に使用する",
        children: [
          { title: "識別名を変更する" },
          {
            title: "通信を暗号化する",
          },
          {
            title: "暗号キーを変更する",
          },
          {
            title: "ルーターの設定は有線のみ有効にする",
          },
          {
            title: "自動アップデートをオンにする",
          },
          {
            title: "フリーWi-Fiは安全？",
          },
        ],
      },
      {
        title: "VPNを使用する",
      },
    ],
  },
];

const { Title, Text } = Typography;
export const DeviceSecurity = () => {
  return (
    <div>
      <ProgressCircle />
      <IndexList list={list} />
      <Title level={3}>
        <span id="デバイスセキュリティ">１．デバイスセキュリティ</span>
      </Title>
      少し原始的な話にも聞こえますが、まずはわたしたちの持っている端末を身の知れない誰かの手に渡らないことが大事です。
      ここではスマホがとられたときに情報漏洩が起らないような対策を学びます。
      <Title level={4}>
        <span id="画面ロックをかける">画面ロックをかける</span>
      </Title>
      <Marker>
        スマホやパソコンには必ず画面ロックをかけるようにしましょう。
      </Marker>
      スクリーンにロックがかかっているだけでも、ロックが秘密である以上、攻撃者はシステムに侵入することが難しくなります。
      ここでも
      <Link to="/lectures/account-security#安全なパスワードの作り方">
        アカウントセキュリティ
      </Link>
      で学んだように、1234（ピンコード
      <Note
        title="「PINコード」（ピンコード）"
        content="狭い意味では、スマホなどを利用するときに打ち込む、暗証番号のようなもの。複数回入力を間違うと明示的な入力遅延や入力画面がロックされるなどの規制がかかるものを指す。
        間違えすぎると強制的にデータを消去する「ワイプ」機能があるものも。"
      />
      ）やZ（パターンロック
      <Note
        title="パターンロック"
        content="スマホをロック解除するときに、画面上に表示される複数の点を、あらかじめ登録したパターンでなぞり、ロックを解除する機能。"
      />
      ）など単純すぎるものは避けてください。
      また文字に比べて選択肢の少ないパターンロックやPINコードでは、スクリーンに残った脂から推測されることがあります。入力後には画面を布などで拭くことも大切です。
      <Marker>
        入力中に後ろからのぞくなどショルダーハックと言われる手口もあるため、パスワードと同様に人に見られないようにしましょう。
      </Marker>
      鍵は厳重であればあるほど安全なので、LINEなどアプリごとに鍵がかけれる場合は二重にロックをしましょう。
      ここでもパスワードの使い回しは避けてください。
      また、いまどきのスマホには指紋認証や顔認証などの生体認証も人気ですが、寝ているときに指を勝手に使われたり写真から顔の復元で通ってしまうという弱点もあります。
      生体認証はショルダーハックなどの攻撃に対して安全ですが、身体の自由がきかないときに悪用されるリスクもあるので過信しないようにしましょう。
      <Card>
        <Title level={4}>
          <span id="画面ロックの時間を変更する">
            画面ロックの時間を変更する <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        画面ロックをかけていても、ロック解除した状態で席を離れてしまってはロックの意味がなくなってしまいます。
        このような事態を防ぐためには面倒かもしれませんがスクリーンロックをすぐ作動させることが大事です。
        <br />
        <br />
        <Tag>Android</Tag>
        <br />
        <b>設定 &gt; セキュリティ &gt; 画面ロック</b>
        <br />
        画面消灯後にロックを「すぐ」など一番早いものにする。
        「電源ボタンですぐにロックする」をオンにする。
        <br />
        <br />
        <Tag>iOS</Tag>
        <br />
        <b>設定 &gt; 画面表示と明るさ &gt; 自動ロック</b>
        <br />
        最も短い「30秒」などに設定する。
      </Card>
      <Title level={4}>
        <span id="紛失や盗難に備える">紛失や盗難に備える</span>
      </Title>
      <ImageDescription
        src="https://i.imgur.com/9Xj4IQw.png"
        description={
          <>
            引用：
            <a
              href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
              target="_blank"
              rel="noreferrer"
            >
              内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／95ページ
            </a>
          </>
        }
      />
      スマホの紛失または盗難の場合には
      <Marker>遠隔からロックできるようにしましょう。</Marker>
      <Card>
        <Title level={4}>
          <span id="遠隔でロックする">
            遠隔でロックする <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        スマホをなくしてしまった・盗まれてしまったという場合にすぐに行わなければいけない動作は以下の通りです。
        実際に起こらないのが一番ですが、もしものためにも訓練だと思って実践してみましょう。
        <br />
        <br />
        <Tag>Android</Tag>
        <br />
        １．まずは以下の使用条件を満たすように設定する
        <ul>
          <li>電源がオンになっている</li>
          <li>モバイルデータまたは Wi-Fi に接続している</li>
          <li> Google Play での表示がオンになっている</li>
          <li>位置情報がオンになっている</li>
          <li>「デバイスを探す」がオンになっている</li>
        </ul>
        ２．実際に端末を探してロックする
        <ol>
          <li>
            <a href="https://android.com/find" target="_blank" rel="noreferrer">
              Android端末を探す
            </a>
            にアクセスする
          </li>
          <li>Googleアカウントにログインする</li>
          <li>「デバイスの保護」をする</li>
        </ol>
        その間にマップにおおよその現在位置が表示されるのですぐに取りに行く準備をし、近くに行ったら「音を鳴らす」などの操作ができます。
        途中、怪しい動作が見られた場合には「デバイスの消去」も可能であるということを覚えておきましょう。
        <a
          href="https://support.google.com/accounts/answer/6160491?hl=ja"
          target="_blank"
          rel="noreferrer"
        >
          公式サイトで手順を見る
        </a>
        <br />
        <br />
        <Tag>iOS</Tag>
        <ol>
          <li>
            <a
              href="https://www.icloud.com/find"
              target="_blank"
              rel="noreferrer"
            >
              iOS端末を探す
            </a>
            にアクセスする
          </li>
          <li>紛失としてマークする</li>
        </ol>
        紛失としてマークすることで遠隔でロックができるので情報を安全に守れます。その後の詳しい手順は公式サイトでも確認してください。
        <a
          href="https://support.apple.com/ja-jp/HT201472"
          target="_blank"
          rel="noreferrer"
        >
          公式サイトで手順を見る
        </a>
      </Card>
      <Title level={4}>
        <span id="暗号化する">暗号化する</span>
      </Title>
      スマホなどの端末が盗まれたら、上のようなロックはもちろん有効ですが最初から端末自体を暗号化
      <Note
        title="暗号化"
        content="文章などを正統な利用者以外が通常の手段では読めないように加工すること。"
      />
      することも大切です。
      <Marker>
        端末に入っているデータのすべてが暗号化された状態であれば、攻撃者に個人情報が漏洩することが防げるからです。
      </Marker>
      <Card>
        <Title level={4}>
          <span id="スマホ端末を暗号化する">
            スマホ端末を暗号化する <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        最近のスマホは最初から暗号化されているものも多いですが、改めて確認しましょう。
        同様にパソコンなどにバックアップする場合も暗号化を適用してください。
        <br />
        <br />
        <Tag>Android</Tag>
        <br />
        <Text strong>Android 8以降からすべてデフォルトで暗号化</Text>
        <br />
        設定 &gt; セキュリティ &gt; 携帯電話の暗号化
        <br />
        <br />
        <Tag>iOS</Tag>
        <br />
        <Text strong>iOS 8以降からすべてデフォルトで暗号化</Text>
      </Card>
      <br />
      ちなみにバックアップ
      <Note
        title="バックアップ"
        content="パソコンやスマホの情報を別途保存しておき、機器が故障したり紛失や盗難したりした場合に、復元するためのもの。
        機器の情報の一括バックアップと、目的のデータ毎のバックアップがある。"
      />
      には 3-2-1 ルールと呼ばれる、
      <Marker>
        少なくとも 3 個以上の 複製、2種類以上の記録メディア、 1
        個は遠い場所に保管する
      </Marker>
      ことが推奨されています。
      <Title level={3}>
        <span id="アプリケーションセキュリティ">
          ２．アプリケーションセキュリティ
        </span>
      </Title>
      スマホ、パソコン、アプリなどは今では生活の一部となっています。
      しかしすべてのサイバー攻撃に対応する完璧なセキュリティ対策はいまだに存在しません。
      ここではサービスを使っていく上でわたしたちにも出来る管理を学びます。
      <Title level={4}>
        <span id="信頼するものを選ぶ">信頼するものを選ぶ</span>
      </Title>
      <Image
        src="https://3.bp.blogspot.com/-G8t4htGXy8g/VXOUdjGtGhI/AAAAAAAAuLI/wU6gnpTOrYA/s400/smartphone_app.png"
        preview={false}
      />
      <br />
      ソフトウェアやアプリをダウンロード・インストールするときは必ず公式サイトから行いましょう。
      信頼できるサイトを見つけるには
      <Link to="/lectures/online-fraud">オンライン詐欺</Link>
      で学んだ方法が役に立ちます。
      また、信頼されていないソフトウェアや怪しいアプリは極力避け、事前に口コミなどで評価を調べることも有効です。
      そして拡張子
      <Note
        title="拡張子"
        content="ファイルの種類を識別するためにファイル名の末尾につけられる文字列。Word文書なら.doc、写真なら.jpgなどがある。"
      />
      も確認するようにしましょう。
      あくまで自分だけの確認では不安になることも多いはずです。
      <Marker>
        手っ取り早く怪しいソフトやアプリを排除するためには、信頼できるウイルス検知ソフトを使う
      </Marker>
      のが最善策です。
      無料のウイルス検知ソフトも数多く存在しますが、残念ながらそれ自体がマルウェアのものも混ざっているのが現状です。
      ウイルス検知ソフトを選ぶときには、自身で評価などの情報を集め、信頼できる公式サイトから確認しましょう。
      さらにインターネットプロバイダーによる
      <Marker>フィルタリング機能</Marker>
      は小さなお子さんにも有効です。一度回線を契約している会社に話を聞いてみるのもよいでしょう。
      <Marker>
        技術的な話を覚える必要は一切なく、とにかくダウンロードやインストールする前に調べることが大事です。
      </Marker>
      <Card>
        <Title level={4}>
          <span id="モバイルアプリのインストールを制限する">
            モバイルアプリのインストールを制限する{" "}
            <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        インターネット上にはGoogle Play（Android）やApple
        Store（iOS）から認証されていないアプリがたくさんあります。もちろん認証されているからといって必ずしも安全とは限りませんが、無認可アプリのインストールを制限するためには以下が有効です。
        <br />
        <br />
        <Tag>Android</Tag>
        <br />
        <b>設定 &gt; セキュリティ &gt; 提供元不明のアプリ</b>
        <br />
        「提供元不明アプリのインストールを許可する」のチェックを外す。
        <br />
        <br />
        <Tag>iOS</Tag>
        <br />
        <b>設定 &gt; 一般 &gt; デバイス管理</b>
        <br />
        「信頼されていないエンタープライズデベロッパ」を「信頼」しない。
      </Card>
      <Title level={4}>
        <span id="アクセス権限を最小限にする">アクセス権限を最小限にする</span>
      </Title>
      セキュリティ対策の基本として与える権限を最小限にするという方法があります。
      例えばただのゲームアプリにカメラ使用の権限は必要ありません。これは誤作動も含め、予期せぬトラブルを防ぐためにも大切です。
      <ImageDescription
        src="https://i.imgur.com/nwwXPBp.png"
        description={
          <>
            引用：
            <a
              href="https://www.nisc.go.jp/security-site/files/handbook-all.pdf"
              target="_blank"
              rel="noreferrer"
            >
              内閣サイバーセキュリティセンター／インターネットの安全・安心ハンドブック／59ページ
            </a>
          </>
        }
      />
      連携時に問題がないとしても更新時に権限の拡張を求め、個人情報を合法的に奪うアプリも存在しています。
      同時にアプリ連携も必要でないものは連携解除または削除するようにしましょう。
      <Card>
        <Title level={4}>
          <span id="アプリの権限を確認する">
            アプリの権限を確認する <Tag color="blue">今すぐ実践</Tag>
          </span>
        </Title>
        アプリの種類によってはカメラやマイクを必要とするものがあります。
        しかし、一部のアプリは必要以上に権限を許可されています。
        盗聴や盗撮を防ぐためにも、改めてアプリの権限を確認してみてください。
        制限をかけることで使用されるソフトウェアが減り、充電を長持ちさせるメリットもあります。
        また普段使用していないアプリも同様の理由からアンインストールしましょう。
        <br />
        <br />
        <Tag>Android</Tag>
        <br />
        <b>設定 &gt; アプリと通知 &gt; 該当アプリをタップ &gt; 権限</b> <br />
        必要に応じて「許可」または「許可しない」を選択する。
        <br />
        <br />
        <Tag>iOS</Tag>
        <br />
        <b>設定 &gt; アプリ一覧 &gt; 該当アプリをタップ &gt; アクセスを許可</b>
        <br />
        必要に応じて「許可」または「許可しない」を選択する。
      </Card>
      <Title level={3}>
        <span id="ネットワークセキュリティ">３．ネットワークセキュリティ</span>
      </Title>
      <Image
        src="https://3.bp.blogspot.com/-KD-vDEF7A5A/VUIJfNA38SI/AAAAAAAAtU4/aDV-_O6jlcY/s400/internet_earth.png"
        preview={false}
      />
      <br />
      わたしたちが普段使用しているインターネットではアカウント情報を含む人に知られてはいけない秘密情報をサーバー{" "}
      <Note
        title="サーバー"
        content="サービスを提供するコンピュータのこと。アプリケーションの裏側でデータのやり取りやファイルの保管などに使われる。"
      />
      とやりとりしています。ここではわたしたちに出来るインターネット通信の暗号化について学びます。
      <Title level={4}>
        <span id="暗号化されたウェブサイトと通信する">
          暗号化されたウェブサイトと通信する
        </span>
      </Title>
      普段からインターネットを利用している人はきっとウェブサイトのURLは
      <Text code>http://</Text>と<Text code>https://</Text>
      があることに気づいていると思います。その違いはセキュリティ面でもかなり重要なのでここでしっかり学びましょう。
      <Title level={5}>
        <span id="HTTPとHTTPS">HTTPとHTTPS</span>
      </Title>
      <ImageDescription
        src="https://japan.norton.com/blog/wp-content/uploads/2017/04/https_intro_image_1_mitm.png"
        description={
          <>
            引用：
            <a
              href="https://japan.norton.com/what-is-https-8564"
              target="_blank"
              rel="noreferrer"
            >
              Norton httpとhttpsの違いとは?
              知っておきたいウェブセキュリティの常識と今後
            </a>
          </>
        }
      />
      インターネット通信（HTTP）の基本は平文
      <Note title="平文" content="暗号化されていない文書のこと。" />
      での送受信です。 ウェブブラウザ
      <Note
        title="ウェブブラウザ"
        content="ネット上で公開されているウェブサーバを閲覧するためのソフトウェアやアプリ。Google Chrome、Firefox、Internet Explorer、Safariなどがある。"
      />
      の上部にあるアドレスバーをよく見たときに、
      <ol>
        <li>
          <Text code>http://</Text>
          から始まっているもの
        </li>
        <li>
          「保護されていません」「安全ではありません」と表示されているもの
        </li>
        <li>
          情報マーク
          <InfoCircleOutlined />
          や壊れた鍵のマーク
          <UnlockOutlined />
          がついているもの
        </li>
      </ol>
      上の場合は<Marker>暗号化されていないまま通信が行われています。</Marker>
      平文での通信の場合は 攻撃者から盗聴や改ざんの恐れがあります。
      それを防ぐためには
      <Marker>ウェブサーバとウェブブラウザ間の通信を暗号化する</Marker>
      必要があります。 一般に「SSL/TLS」
      <Note
        title="SSL/TLS"
        content="Secure Socket Layer / Transport Layer Security。データを暗号化して送受信する方法のこと。"
      />
      という暗号化通信が行われている場合はウェブサイトのURLは
      <Text code>https://</Text>から始まります。
      ちなみに最後のsはsecure（安全な）の頭文字です。
      <Text code>https://</Text>
      で始まるウェブサイトはあくまで一応安心というだけで、最近ではこの状態でも安全を保証できないことが分かりました。
      <Title level={5}>
        <span id="SSL証明書を確認する">SSL証明書を確認する</span>
      </Title>
      暗号化通信のためには認証局がサイト運営者を審査し、信頼できると判断されたときにSSL証明書
      <Note
        title="SSL証明書"
        content="SSLで通信を行うサーバの身分証明書のようなもの。認証局が審査を行って発行する。
        最近は審査がいい加減だったり、無料で発行する認証局の登場により、安全であることの目安とはならない状況になりつつある。
        より審査の厳しいEV-SSL証明書も存在する。"
      />
      という電子証明書を発行します。
      しかし、一部のSSL証明書の中では実在性すら確認しないような甘い認証局につけこんだものもあり、攻撃者は一見安全に見える詐欺サイトを立ち上げるようになってしまいました。
      そんな背景からわたしたちは実際にSSL証明書を確認する必要があります。確認方法はいたってシンプルで、
      <Marker>
        ブラウザの上部のアドレスバーにある鍵マーク　&gt;　証明書の順にクリックする
      </Marker>
      だけです。
      ここで、証明書の使い方が間違っている場合はブラウザからの警告が表示され、さらに有効期限が切れている場合は失効と表示されます。
      <ImageDescription
        src="https://i.imgur.com/PwIg6PI.png"
        description={
          <>
            Google Chromeブラウザから<a href="https://google.com">Googl.com</a>
            の証明書を確認した場合、
            <Text type="success" strong>
              この接続は保護されています
            </Text>
            と表示される。
          </>
        }
      />
      そのようなウェブサイトは
      <Marker>
        セキュリティに関する意識が低いので安全に利用できないと考える
      </Marker>
      のが大切です。
      <Title level={5}>
        <span id="さらに安全なEV-SSL証明書の登場">
          さらに安全なEV-SSL証明書の登場
        </span>
      </Title>
      <ImageDescription
        src="https://japan.norton.com/blog/wp-content/uploads/2017/04/https_intro_image_9_dangerous_https-1.png"
        description={
          <>
            引用：
            <a
              href="https://japan.norton.com/what-is-https-8564"
              target="_blank"
              rel="noreferrer"
            >
              Norton httpとhttpsの違いとは?
              知っておきたいウェブセキュリティの常識と今後
            </a>
          </>
        }
      />
      SSL証明書によりウェブブラウザとサーバー間の通信は暗号化が保証されるようになりましたが、通信相手が正しいかの判断ができません。
      そこで外部からの厳しい監査の基準を満たした認証局だけが発行できるEV-SSL証明書というものが登場しました。
      EV-SSL証明書では第三者が所有者の実在性なども確認するため、EV-SSL証明書に対応したブラウザから企業名が表示されることもあります。
      <ImageDescription
        src="https://i.imgur.com/WjAfQTz.png"
        description={
          <>
            Google Chromeブラウザから
            <a href="https://www.rakuten-bank.co.jp/">楽天銀行</a>
            の証明書を確認した場合、
            <Text type="success" strong>
              この接続は保護されています
            </Text>
            の文字と共に、発行先に
            <Text strong>Rakuten Bank, Ltd. [JP]</Text>と表示される。
          </>
        }
      />
      EV-SSL証明書を利用しているサイトの場合は、
      <Marker>
        ドメインだけでなく証明書を保持している企業や団体の所在地も表示されます。
      </Marker>
      さらに細かく見たい場合は、<b>証明書 &gt; 詳細 &gt; サブジェクト</b>
      の中に発行先
      <Text code>O = 会社名</Text>が表示されることを確認してください。
      特にインターネットバンキングなどの金融関連の手続きをする場合は
      <Marker>
        EV-SSL証明書が発行されているかの確認をし、安全性を担保したうえで個人情報を入力するようにしましょう。
      </Marker>
      この手順は
      <Link to="/lectures/online-fraud">オンライン詐欺</Link>
      を防ぐためにも非常に有効です。
      <Title level={4}>
        <span id="Bluetoothを安全に使用する">Bluetoothを安全に使用する</span>
      </Title>
      iOS機器同士でファイル共有ができるAirDropにも使われるBluetooth
      <Note
        title="Bluetooth"
        content="近距離の機器同士を接続するための無線通信技術。"
      />
      。
      せいぜい10メートル程度の範囲内でしか使われませんが、Bluetoothに潜む危険はいくつかあります。
      既に修正されましたが、2017年には脆弱性が見つかり世界中でたくさんのIT機器が危険にさらされました。
      <Marker>
        Bluetoothを使用する必要がないときはオフにするようにしてください。
      </Marker>
      また、BluetoothがオンになっているときはBluetoothの範囲内にいる人にデバイス名が表示されます。
      <Marker>
        デバイス名は本名など個人情報となり得るものは入れないようにしましょう。
      </Marker>
      <Title level={4}>
        <span id="無線LANを安全に使用する">
          無線LAN
          <Note
            title="無線LAN"
            content="ネットで用いられる通信に、無線の信号を用いるもの。
            LAN は Local Area Network の略で、通常は会社や家など小さい単位で用いる。
            インターネットとはルータを境にネットワーク的には分離されている（データの行き来は可能）。
            これに対して広範囲を対象とするネットワークはWAN(Wide Area Network) と呼ぶ"
          />
          を安全に使用する
        </span>
      </Title>
      <Image
        src="http://2.bp.blogspot.com/-KwGSRNHzsVA/UbVvOo58z5I/AAAAAAAAUsc/y2sh_GfnPQ8/s180-c/computer_wireless.png"
        preview={false}
      />
      <br />
      <Link to="/lectures/online-fraud">オンライン詐欺</Link>
      の講座で偽サイトを見抜く方法を学んでいても、ネットワークが安全でない場合には攻撃者による盗聴が行われることがあります。
      <b>無線LANアクセスポイント</b>
      <Note
        title="無線 LANアクセスポイント"
        content="無線LANで通信するために、使用している機器を接続する先、およびその機器。"
      />
      また一般には<b>Wi-Fiルーター</b>
      <Note
        title="ルーター"
        content="インターネットなどを利用するために利用者が接続・経由する機器。
        会社や家庭で利用する無線 LAN アクセスルータのほか、高速な WAN の回線を利用して、おもに屋外などでノートパソコンなどを接続して利用するモバイルルータがある。
        また、有線だけで利用する有線ルータもある。"
      />
      と呼ばれる機器で安全に通信を行う場合には下記のような設定が必要です。
      <Title level={5}>
        <span id="識別名を変更する">１．識別名を変更する</span>
      </Title>
      識別名（SSID）はWi-Fiの名前のことです。Wi-Fiの範囲内であれば誰でも識別名が見えるので必ず変更しましょう。
      <Marker>
        この識別名は家族の名前などが分かる語句の使用は避け、誰が使っているのか分からないような名前にしてください。
      </Marker>
      もし攻撃者があなた個人を狙っている場合、攻撃対象が特定できてしまうからです。
      <Title level={5}>
        <span id="通信を暗号化する"> ２．通信を暗号化する</span>
      </Title>
      <Image
        src="https://4.bp.blogspot.com/--RVxra21NkU/VrBAgZ0xjTI/AAAAAAAA3u8/0LAM956iqKM/s400/touchou.png"
        preview={false}
      />
      <br />
      通信が平文{" "}
      <Note
        title="平文"
        content="暗号化されていないそのままの文章のこと。"
      />{" "}
      のとき、同じネットワーク内
      <Note
        title="同じネットワーク内"
        content="同じWi-Fiにつないでいる人。"
      />{" "}
      にいる攻撃者からの盗聴が可能になってしまいます。これを防ぐためには
      <Marker>
        暗号化といって仮に盗聴されていても元々の文章が分からないようにする必要があります。
      </Marker>
      暗号化されていない通信はわたしたちが思っている以上に盗聴が容易です。
      どんなサイトを見ているかはもちろん、常時暗号化通信を採用していないウェブサイトであればアカウント情報などの個人情報の漏洩も考えられます。（参照：
      <a href="#HTTPとHTTPS">HTTPとHTTPS</a>） 暗号化方式
      <Note
        title="暗号化方式"
        content="暗号化の方式。暗号化するときには利用する暗号化方式の安全性に注意が必要。"
      />{" "}
      にも種類がありますが、 現時点で安全な
      <Marker>WPA-PSK（AES）、WPA2-PSK（AES）、WPA3</Marker>
      を使うようにしてください。 暗号化キー
      <Note
        title="暗号化キー"
        content="暗号化と復号のために利用する鍵となる文字列。
        短く複雑でない暗号化キーは総当たりによって探り当てられやすい。
        また、なんらかの理由で流出したり、意図せず共有すると、キーを入手したものによって暗号化した内容が復号される。"
      />{" "}
      にはセキュリティ上の不安があるTKIPではなく、より安全な
      <Marker>AES</Marker>
      を使用してください。残念ながら以前使われていたWEP、WPAは既に解読されているため使っていても暗号化されていないのと同じです。
      <Title level={5}>
        <span id="暗号キーを変更する"> ３．暗号キーを変更する</span>
      </Title>
      暗号キーはWi-Fiのパスワードのことです。
      これも識別名と同様、必ず自分で設定し直すようにしてください。
      これはルーターによっては共通のパスワードを使ったり規則性があったりと推測可能になるのを防ぐためです。
      極端な話、メーカーから自宅までに届く間、誰かが盗み見した可能性もゼロとは言い切れません。これはどのIT機器に関しても通用する話なので初期設定のまま使うのは避けましょう。
      そして<Marker>暗号キーは長く複雑で推測不可能なもの</Marker>
      を使用しましょう。（
      <Link to="/lectures/account-security#安全なパスワードの作り方">
        アカウントセキュリティー安全なパスワードの作り方
      </Link>
      ） また暗号キーは通信の復号
      <Note
        title="復号"
        content="暗号化されたデータを、暗号キーを使って元に戻すこと。"
      />{" "}
      にも使われるため、第一に
      <Marker>不特定多数の人との共有を避ける</Marker>
      ことが大事です。たとえば、家庭内でのWi-Fiの暗号キーを家族以外に知らせてはいけません。
      <Title level={5}>
        <span id="ルーターの設定は有線のみ有効にする">
          ４．ルーターの設定は有線のみ有効にする
        </span>
      </Title>
      <Image
        src="https://1.bp.blogspot.com/-YkvkwkqgXfI/Xlyf0zBjGNI/AAAAAAABXps/WT6CmO-bffQj0SURjXXVkIjJUmSE9OyHgCNcBGAsYHQ/s400/internet_computer_wired.png"
        preview={false}
      />
      <br />
      <Marker>ルーターの設定は有線のみで行うように設定する</Marker>
      ことができます。こうすることで、仮に無線LAN内に侵入者が来ても設定を変えることができなくなります。
      同様の理由でWPS、AOSS、無線LANらくらくスタートのような
      <Marker>ボタン一つで無線LANにアクセスできてしまう機能はオフ</Marker>
      にしてください。設定が簡単ということは不意に家庭内で設定が変えられてしまう可能性があるからです。
      <Title level={5}>
        <span id="自動アップデートをオンにする">
          ５．自動アップデートをオンにする
        </span>
      </Title>
      <Marker>
        ファームウェア
        <Note
          title="復号"
          content="利用する機器のソフトウェアやアプリではなく、機器自身を動かすプログラム。
          ソフトウェアやアプリだけでなく、更新されたら必ずアップデートしなければならないもの。"
        />{" "}
        やソフトウェアの自動アップデート
        <Note
          title="アップデート"
          content="セキュリティ改善要素が含まれているかどうかは関係なく、ソフトウェアやアプリの更新。"
        />{" "}
        をオンにする
      </Marker>
      にすることで仮にルーターに脆弱性が見つかったときにすぐアップデートされます。
      逆に脆弱性をそのままにしておくと、攻撃者に隙を与えてしまうので絶対にアップデートは欠かさないようにしましょう。
      <Card>
        <Title level={4}>
          <span id="フリーWi-Fiは安全？">フリーWi-Fiは安全？</span>
        </Title>
        街中にありふれたフリーWi-Fiは安全でしょうか？<Marker>答えはNO</Marker>
        です。 もし公衆無線LAN
        <Note
          title="公衆無線LAN"
          content="街中や店舗などで、不特定多数に対して インターネット接続環境を提供する無線LANのこと"
        />{" "}
        が上に挙げた安全な暗号化方式を使っているとしても、暗号キーを知らない人と共有することになるので、
        <Marker>
          暗号キーを知っている攻撃者によって通信内容を直接盗聴されるリスクがあります。
        </Marker>
        しかし、暗号キーを他人と共有しないで個別の暗号キーを用いる方式なら安全に使用できます。
        その場合は
        <Marker>
          「WPA2-EAP、WPA2- エン タープライズ、IEEE 802.1x、SIM 認証」
        </Marker>
        といった用語が含まれているので、提供先の
        ウェブサイトなどで確認してください。そもそも暗号キーが存在しない（パスワードがいらない）場合は論外です。
        どうしても使用したい場合は次項のVPNを使って個別に通信を暗号化することができますが、
        <Marker>
          特に仕事などで機密情報を扱う場合は公共のフリーWi-Fiの使用は避け、モバイルホットスポット機能を利用しましょう。
        </Marker>
      </Card>
      <Title level={4}>
        <span id="VPNを使用する">
          VPNを使用する <Tag color="orange">上級者向け</Tag>
        </span>
      </Title>
      少し上級者向けの話になりますが、
      <Marker>
        個人のデバイスから安全な場所にある出口サーバまでまとめて暗号化するVPN（仮想プライベートネットワーク）
      </Marker>
      というものがあります。
      この場合、無線LANやブラウザが安全でなくても攻撃者に盗聴される心配は少なくなります。
      しかし、この技術はまだ一般の利用者が簡単に使えるようになっておらず、どうしても導入にスキルが必要になります。
      また内閣サイバーセキュリティセンター（NISC）によると、利用するVPNサービスによっては暗号化されずに不安定なまま通信を継続してしまうものもあり、
      <Marker>
        2020年春時点では確実なVPNサービスはないというのが現状です。
      </Marker>
      どうしてもVPNを使用したい場合はVPNサービスのテスト結果をしっかり吟味して導入するか、知識がある知り合いに頼むのが最適です。今後は一般利用者にも広まる技術として期待できるかもしれません。
    </div>
  );
};

export default DeviceSecurity;
